import React, { memo } from 'react';
import { useTableContext } from '../../context/TableContext';
import { DEFAULT_STATUSES } from '../../../FlowchartView/TaskNode/constants';
import StatusDropdown from '../../../shared/StatusDropdown';

/**
 * Status cell with dropdown for status selection
 * Uses the shared StatusDropdown component for consistency across views
 * Memoized to prevent re-renders when other cells change
 */
const StatusCell = memo(({ row }) => {
  const { 
    statuses = DEFAULT_STATUSES, 
    allTasks = [], 
    nestedSprints = [],
    lockStates = {},
    onStatusChange, 
    onAddStatus, 
    onDeleteStatus, 
    onRenameStatus,
    onSetStatusAutoSync,
    onSubmitDeliverable,
    onFocusTask,
    onNavigateToParentMission,
  } = useTableContext();
  const task = row.original;
  
  // Get nested sprint info for this task
  const nestedSprint = task.nestedSprintId 
    ? nestedSprints.find(s => s.id === task.nestedSprintId) 
    : null;
  
  // Get lock state
  const lockState = lockStates[task.id];
  const isLocked = lockState?.isLocked && task.status !== 'completed';
  const blockingDeps = lockState?.blockingTasks || [];

  return (
    <StatusDropdown
      variant="badge"
      taskId={task.id}
      taskName={task.name || task.title || 'this mission'}
      currentStatus={task.status}
      statuses={statuses}
      allTasks={allTasks}
      isLocked={isLocked}
      lockReason={lockState?.lockReason}
      blockingDependencies={blockingDeps}
      parentMissionName={lockState?.parentMissionName}
      parentCampaignName={lockState?.parentCampaignName}
      onGoToParentMission={
        lockState?.parentMissionId && lockState?.parentSprintId && onNavigateToParentMission
          ? () => onNavigateToParentMission({
              parentSprintId: lockState.parentSprintId,
              parentMissionId: lockState.parentMissionId,
            })
          : undefined
      }
      onGoToDependency={onFocusTask}
      hasNestedSprint={!!nestedSprint}
      nestedSprintProgress={nestedSprint?.progress}
      statusAutoSync={task.statusAutoSync}
      onStatusChange={onStatusChange}
      onSetStatusAutoSync={onSetStatusAutoSync}
      onAddStatus={onAddStatus}
      onDeleteStatus={onDeleteStatus}
      onRenameStatus={onRenameStatus}
      deliverables={task.deliverables || []}
      onSubmitDeliverable={onSubmitDeliverable}
    />
  );
}, (prevProps, nextProps) => {
  const prevTask = prevProps.row.original;
  const nextTask = nextProps.row.original;
  return prevTask.id === nextTask.id && 
         prevTask.status === nextTask.status && 
         prevTask.statusAutoSync === nextTask.statusAutoSync &&
         prevTask.deliverables === nextTask.deliverables;
});

export default StatusCell;
